// Pago.tsx
import React, { useEffect, useRef, useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { useNavigate, useParams } from "react-router-dom";
import { calcularTotalConIVA } from "../../../utils/calcularTotalIva";
import { reservarNumero, subirContrato, subirFactura } from "../../../utils";
import { PagoProps, PaymentOption } from "@/types";
import Alerta from "../../../alert/Alerta";
import { ButtonPopup } from "../../../ui";
import CardInput from "./CardInput";
import ContratoServicios, {
  ContratoPdfRef,
} from "../contratoServicios/ContratoServicios";
import FacturaPDF, { FacturaPdfRef } from "../contratoServicios/FacturaPDF";
/* import { APP_CONFIG } from '@/config/appConfig'; */
import { safeFetch } from "@/api/safeFetch";

const Pago: React.FC<
  PagoProps & {
    onClosePopup?: () => void;
    modoRenovar?: boolean;
    paymentOption?: PaymentOption;
    codigoPromocional?: string | null;
  }
> = ({
  totalPago,
  setTotalPago,
  iva = 21,
  onTotalPagarChange,
  onClosePopup,
  firmaBase64,
  selectedProfesiones,
  formData1,
  precioOriginal,
  isEmpresa,
  onPagoExitoso,
  modoRenovar = false,
  paymentOption: paymentOptionProp,
  codigoPromocional,
}) => {
  const [name, setName] = useState("");
  const [focus, setFocus] = useState<"number" | "name" | "expiry" | "cvc" | "">(
    "",
  );
  const [alerta, setAlerta] = useState<string | null>(null);
  const [totalConIVA, setTotalConIVA] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [descuento, setDescuento] = useState<number>(0);
  const [transactionId, setTransactionId] = useState<string | null>(null);
  const [numeroContrato, setNumeroContrato] = useState<string | null>(null);
  const [numeroFactura, setNumeroFactura] = useState<string | null>(null);
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const {
    perfil_profesional_id: paramPerfilId,
    profesional_id: paramProfesionalId,
  } = useParams<{ perfil_profesional_id: string; profesional_id: string }>();
  const contratoRef = useRef<ContratoPdfRef>(null);
  const facturaRef = useRef<FacturaPdfRef>(null);
  const [paymentOption, setPaymentOption] = useState<PaymentOption>(
    paymentOptionProp ?? "Anual",
  );
  const ahora = new Date(); // Fecha única para todo el proceso
  const fechaEsp = ahora.toLocaleDateString("es-ES");
  const esGratis = totalConIVA <= 0;
  const paymentLock = useRef(false);
  const paymentSucceeded = useRef(false);
  const alreadyLogged = !!localStorage.getItem("token");
  const paymentIdempotencyKey = useRef<string | null>(null);
  const [paymentCompleted, setPaymentCompleted] = useState(false);

  useEffect(() => {
    if (paymentOptionProp) setPaymentOption(paymentOptionProp);
  }, [paymentOptionProp]);

  useEffect(() => {
    const baseEuros = totalPago ?? 0;
    const total = calcularTotalConIVA(baseEuros, iva);
    setTotalConIVA(total);
    setTotalPago?.(baseEuros);
    onTotalPagarChange?.(total);

    if (precioOriginal && precioOriginal > baseEuros) {
      const porcentaje = ((precioOriginal - baseEuros) / precioOriginal) * 100;
      setDescuento(Number(porcentaje.toFixed(2)));
    } else setDescuento(0);
  }, [totalPago, iva, precioOriginal]);

  // ------------------------
  // handlePayment principal (completo)
  // ------------------------
  const handlePayment = async () => {
    // 🛑 Ya hay un pago en curso o este pago ya terminó correctamente
    if (paymentLock.current || paymentSucceeded.current || loading) {
      return;
    }

    // 🔒 Bloqueo INMEDIATO
    paymentLock.current = true;
    setLoading(true);
    setAlerta(null);

    // 🔑 Crear una clave única para ESTE intento de pago
    if (!paymentIdempotencyKey.current) {
      paymentIdempotencyKey.current = crypto.randomUUID();
    }

    // 🔒 Bloqueo INMEDIATO antes de cualquier operación async
    paymentLock.current = true;
    setLoading(true);
    setAlerta(null);

    if (!stripe || !elements) {
      paymentLock.current = false;
      setLoading(false);
      setAlerta("Stripe no está cargado.");
      return;
    }

    // Limpiar sessionStorage
    sessionStorage.removeItem("pdfContratoBase64");
    sessionStorage.removeItem("pdfFacturaBase64");
    sessionStorage.removeItem("numeroContrato");
    sessionStorage.removeItem("numeroFactura");

    const esGratis = totalConIVA <= 0;

    try {
      // ============================================================
      // 🟢 FLUJO GRATUITO
      // ============================================================
      if (esGratis) {
        let perfilId =
          formData1?.perfil_profesional_id ??
          (paramPerfilId ? Number(paramPerfilId) : undefined);
        let profesionalId =
          formData1?.profesional_id ??
          (paramProfesionalId ? Number(paramProfesionalId) : undefined);
        let usuarioId = undefined;

        // 1️⃣ Crear suscripción gratuita
        const susRes = await safeFetch(`/api/nuevaSuscripcion`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData1,
            profesional_id: formData1?.profesional_id ?? profesionalId,
            perfil_profesional_id: formData1?.perfil_profesional_id ?? perfilId,
            usuario_id: formData1?.usuario_id ?? usuarioId,
            selectedProfesiones,
            paymentOption,
            paymentAmount: 0,
            payment_intent_id: null,
          }),
        });

        if (!susRes.ok)
          throw new Error(`Error creando suscripción: ${susRes.status}`);
        const susJson = await susRes.json();

        // 2️⃣ Guardar token nuevo
        if (susJson.idToken) {
          localStorage.setItem("token", susJson.idToken);

          if (alreadyLogged) {
            window.dispatchEvent(new Event("tokenChanged"));
          }
        }

        // 3️⃣ IDs reales
        usuarioId = susJson.usuario_id ?? usuarioId;
        profesionalId = susJson.profesional_id ?? profesionalId;
        perfilId = susJson.perfil_profesional_id ?? perfilId;

        if (!usuarioId || !profesionalId || !perfilId) {
          throw new Error("IDs inválidos antes de subir contrato");
        }

        // 4️⃣ Reservar número
        const numeroContratoReservado = await reservarNumero(
          "contrato",
          profesionalId,
          perfilId,
          usuarioId,
        );

        // 5️⃣ Guardar pago gratuito
        await safeFetch(`/api/pago/guardarPago`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            profesional_id: profesionalId,
            perfil_profesional_id: perfilId,
            amount_total: 0,
            status: "gratis",
            payment_intent_id: null,
            session_id: null,
            codigo: codigoPromocional ?? formData1?.codigoAplicado ?? undefined,
          }),
        });

        // 6️⃣ Guardar firma
        if (firmaBase64 && perfilId) {
          await safeFetch(`/api/upload/firmas`, {
            method: "POST",
            body: JSON.stringify({
              image: firmaBase64,
              perfil_profesional_id: perfilId,
            }),
          });
        }

        setNumeroContrato(numeroContratoReservado ?? null);

        // ⬇️ esperar render React
        await new Promise((resolve) => setTimeout(resolve, 50));

        // 7️⃣ Generar y subir contrato
        if (
          contratoRef.current?.generarPdfComoBase64 &&
          numeroContratoReservado
        ) {
          const contratoBase64 = await contratoRef.current.generarPdfComoBase64(
            numeroContratoReservado,
            undefined,
          );

          if (contratoBase64) {
            await subirContrato(
              contratoBase64,
              {
                profesional_id: profesionalId,
                usuario_id: usuarioId ?? 0, // evita undefined
                perfil_profesional_id: perfilId,
                cantidad: 0,
                metodo_pago: "Promoción Amigo",
              },
              numeroContratoReservado, // transactionId como antes
              `${numeroContratoReservado}.pdf`,
            );

            sessionStorage.setItem("pdfContratoBase64", contratoBase64);
            sessionStorage.setItem("numeroContrato", numeroContratoReservado);
          }
        }

        // 8️⃣ Email
        await safeFetch("/api/enviarCorreoSuscripcion", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            profesional_id: profesionalId,
            usuario_id: usuarioId,
            payment_intent_id: null,
            numeroContrato: numeroContratoReservado,
            numeroFactura: null,
          }),
        }).catch(() => {});

        // 9️⃣ Éxito
        setAlerta(
          "🎉 Enhorabuena, tu suscripción se ha activado gratuitamente",
        );
        onPagoExitoso?.();

        if (!alreadyLogged) {
          setTimeout(() => {
            localStorage.removeItem("token");
          });
        }

        // flujo GRATIS
        window.dispatchEvent(new Event("userDataUpdated"));

        setTimeout(() => {
          onClosePopup?.();
          navigate("/success", {
            state: {
              payment: { amount: 0, status: "gratis", transactionId: null },
              totalPagar: 0,
            },
          });
        }, 1200);

        return;
      }

      // ============================================================
      // 🔵 FLUJO STRIPE
      // ============================================================
      const card = elements.getElement(CardNumberElement);
      if (!card) throw new Error("No se pudo obtener el campo de tarjeta.");

      // 1️⃣ Crear PaymentIntent
      // 1️⃣ Crear PaymentIntent
      const piRes = await safeFetch(`/api/pago/create-payment-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: Math.round(totalConIVA * 100),
          idempotencyKey: paymentIdempotencyKey.current,
        }),
      });

      if (!piRes.ok) {
        const errorJson = await piRes.json().catch(() => null);

        throw new Error(
          errorJson?.error || `Error creando PaymentIntent: ${piRes.status}`,
        );
      }

      const piJson = await piRes.json();

      const clientSecret = piJson.data?.clientSecret;

      if (!clientSecret) {
        throw new Error("No se recibió el clientSecret");
      }

      // 2️⃣ Confirmar pago
      const { error, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card,
            billing_details: { name: name || "Sin nombre" },
          },
        },
      );

      if (error) throw new Error(error.message);
      if (!paymentIntent || paymentIntent.status !== "succeeded") {
        throw new Error("El pago no fue exitoso");
      }

      const piId = paymentIntent.id;

      // 🔒 Stripe ya ha confirmado el pago.
      // Desde este momento NO permitimos otro intento desde este componente.
      paymentSucceeded.current = true;
      setPaymentCompleted(true);

      const paymentMethodId =
        typeof paymentIntent.payment_method === "string"
          ? paymentIntent.payment_method
          : paymentIntent.payment_method?.id;

      let perfilId =
        formData1?.perfil_profesional_id ??
        (paramPerfilId ? Number(paramPerfilId) : undefined);
      let profesionalId =
        formData1?.profesional_id ??
        (paramProfesionalId ? Number(paramProfesionalId) : undefined);
      let usuarioId = undefined;

      // 3️⃣ Crear suscripción
      const susUrl = modoRenovar ? `/api/renovar` : `/api/nuevaSuscripcion`;

      const susBody = modoRenovar
        ? {
            perfil_profesional_id: perfilId,
            profesional_id: profesionalId,
            usuario_id: formData1?.usuario_id || 0,
            monto: Number(totalConIVA.toFixed(2)),
            paymentOption,
            payment_intent_id: piId ?? null,
          }
        : {
            ...formData1,
            profesional_id: formData1?.profesional_id ?? profesionalId,
            perfil_profesional_id: formData1?.perfil_profesional_id ?? perfilId,
            usuario_id: formData1?.usuario_id ?? usuarioId,
            selectedProfesiones,
            paymentOption,
            paymentAmount: Number(totalConIVA.toFixed(2)),
            payment_intent_id: piId ?? null,
          };

      const susRes = await safeFetch(susUrl, {
        method: modoRenovar ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(susBody),
      });

      if (!susRes.ok)
        throw new Error(`Error creando suscripción: ${susRes.status}`);

      const susJson = await susRes.json();

      usuarioId = susJson.usuario_id;
      profesionalId = susJson.profesional_id;
      perfilId = susJson.perfil_profesional_id;

      // 4️⃣ Guardar token nuevo
      if (susJson.idToken) {
        localStorage.setItem("token", susJson.idToken);

        if (alreadyLogged) {
          window.dispatchEvent(new Event("tokenChanged"));
        }
      }

      if (!usuarioId || !profesionalId || !perfilId) {
        throw new Error("IDs inválidos antes de subir contrato");
      }

      // 6️⃣ Guardar pago
      await safeFetch(`/api/pago/guardarPago`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profesional_id: profesionalId,
          perfil_profesional_id: perfilId,
          amount_total: Number(totalConIVA.toFixed(2)),
          status: paymentIntent.status,

          payment_intent_id: piId,
          payment_method_id: paymentMethodId,

          session_id: null,
          codigo: codigoPromocional ?? undefined,
        }),
      });

      // 7️⃣ Guardar firma
      if (firmaBase64 && perfilId) {
        await safeFetch(`/api/upload/firmas`, {
          method: "POST",
          body: JSON.stringify({
            image: firmaBase64,
            perfil_profesional_id: perfilId,
          }),
        });
      }

      // 8️⃣ Reservar números
      const numeroContratoReservado = await reservarNumero(
        "contrato",
        profesionalId,
        perfilId,
        usuarioId,
      );
      const numeroFacturaReservada = await reservarNumero(
        "factura",
        profesionalId,
        undefined,
        usuarioId,
      );

      setNumeroContrato(numeroContratoReservado ?? null);
      setNumeroFactura(numeroFacturaReservada ?? null);

      // 9️⃣ Generar y subir contrato
      if (contratoRef.current?.generarPdfComoBase64) {
        const contratoBase64 = await contratoRef.current.generarPdfComoBase64(
          numeroContratoReservado,
          numeroFacturaReservada,
        );

        if (contratoBase64) {
          await subirContrato(
            contratoBase64,
            {
              profesional_id: profesionalId,
              usuario_id: usuarioId,
              perfil_profesional_id: perfilId,
            },
            piId,
            numeroContratoReservado
              ? `${numeroContratoReservado}.pdf`
              : undefined,
          );

          sessionStorage.setItem("pdfContratoBase64", contratoBase64);
        }
      }

      // 🔟 Generar y subir factura
      if (facturaRef.current?.generarPdfComoBase64) {
        const facturaBase64 = await facturaRef.current.generarPdfComoBase64(
          numeroFacturaReservada,
        );
        if (facturaBase64) {
          await subirFactura(
            facturaBase64,
            {
              profesional_id: profesionalId,
              usuario_id: usuarioId,
              perfil_profesional_id: perfilId,
            },
            piId,
            Number(totalConIVA.toFixed(2)),
            numeroFacturaReservada
              ? `${numeroFacturaReservada}.pdf`
              : undefined,
          );

          sessionStorage.setItem("pdfFacturaBase64", facturaBase64);
        }
      }

      // 1️⃣1️⃣ Email
      await safeFetch("/api/enviarCorreoSuscripcion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profesional_id: profesionalId,
          usuario_id: usuarioId,
          payment_intent_id: piId,
          numeroContrato: numeroContratoReservado,
          numeroFactura: numeroFacturaReservada,
        }),
      }).catch(() => {});

      window.dispatchEvent(new Event("userDataUpdated"));
      // 1️⃣2️⃣ Éxito
      setTransactionId(piId);
      setAlerta("✅ Pago y suscripción realizados con éxito");
      onPagoExitoso?.();

      if (!alreadyLogged) {
        setTimeout(() => {
          localStorage.removeItem("token");
        });
      }

      setTimeout(() => {
        onClosePopup?.();
        navigate("/success", {
          state: {
            payment: {
              amount: paymentIntent.amount,
              status: paymentIntent.status,
              transactionId: piId,
            },
            totalPagar: totalConIVA,
          },
        });
      }, 1200);
    } catch (e: any) {
      console.error("ERROR handlePayment:", e);

      // Si Stripe NO llegó a confirmar el pago,
      // permitimos volver a intentarlo.
      if (!paymentSucceeded.current) {
        paymentLock.current = false;
        paymentIdempotencyKey.current = null;
      }

      setLoading(false);

      setAlerta(e.message ?? "Error desconocido durante el pago");

      // ⚠️ No cerramos inmediatamente el popup.
      // El usuario debe poder corregir la tarjeta y volver a intentarlo.
    } finally {
      if (!paymentSucceeded.current) {
        paymentLock.current = false;
      }

      setLoading(false);
    }
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="p-2">
      {alerta && (
        <Alerta
          mensaje={alerta}
          type="success"
          onClose={() => setAlerta(null)}
        />
      )}

      {esGratis ? (
        <div className="flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col items-center gap-2 my-4">
            <span className="text-3xl animate-bounce">😄</span>
            <p className="text-lg font-semibold text-white text-center">
              ¡Qué suerte la tuya! Tu suscripción es{" "}
              <span className="text-green-400">gratuita</span>.
            </p>
          </div>

          <ButtonPopup
            onClick={handlePayment}
            disabled={loading || paymentCompleted}
            className={`btn ${
              loading || paymentCompleted ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {loading
              ? "Procesando..."
              : paymentCompleted
                ? "Pago realizado"
                : "Pagar"}
          </ButtonPopup>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-center gap-8 mb-6">
            <img
              src="/icons/3D.png"
              alt="3D Secure"
              className="h-14 max-w-[60px]"
            />
            <img
              src="/img/logo/LogoLinkServs.png"
              alt="Logo LinkServs"
              className="w-20"
            />
            <img src="/icons/ssl.png" alt="SSL" className="h-14 max-w-[60px]" />
          </div>

          <div className="flex flex-wrap justify-center gap-8 mb-4">
            <img
              src="/icons/visa-mastercard.png"
              alt="Visa y Mastercard"
              className="h-14 max-w-[80px]"
            />
            <img
              src="/icons/American-Express.png"
              alt="American Express"
              className="h-14 max-w-[80px]"
            />
            <img
              src="/icons/lock.png"
              alt="Seguro"
              className="h-14 max-w-[80px]"
            />
          </div>

          <CardInput
            name={name}
            setName={setName}
            focus={focus}
            setFocus={setFocus}
          />

          <p className="text-lg font-semibold my-3 text-white">
            Total a pagar: {totalConIVA.toFixed(2)} €
          </p>

          <ButtonPopup
            onClick={handlePayment}
            disabled={loading}
            className={`btn ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {loading ? "Procesando..." : "Pagar"}
          </ButtonPopup>
        </>
      )}

      {/* CONTRATO Y FACTURA */}
      <div
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "210mm",
          zIndex: -1000,
          overflow: "visible",
        }}
      >
        <ContratoServicios
          ref={contratoRef}
          selectedProfesiones={selectedProfesiones}
          formData1={formData1}
          firmaBase64={firmaBase64}
          firmaUrl={null}
          paymentMethod={paymentOption}
          paymentOption={paymentOption}
          city="España"
          isEmpresa={isEmpresa}
          currentDate={fechaEsp}
          profesional_id={formData1?.profesional_id}
          perfil_profesional_id={formData1?.perfil_profesional_id}
          numeroContrato={numeroContrato ?? ""}
          numeroFactura={numeroFactura ?? ""}
          totalPagar={totalConIVA}
        />

        <FacturaPDF
          ref={facturaRef}
          selectedProfesiones={selectedProfesiones}
          formData1={formData1}
          paymentMethod={paymentOption}
          paymentOption={paymentOption}
          currentDate={fechaEsp}
          isEmpresa={isEmpresa}
          transactionId={transactionId ?? ""}
          precioOriginal={precioOriginal}
          descuento={descuento}
          numeroFactura={numeroFactura ?? ""}
        />
      </div>
    </div>
  );
};

export default Pago;
